import styled from '@emotion/styled';
import { WarningAmber } from '@mui/icons-material';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Modal,
  Stack,
  Tooltip,
  tooltipClasses,
  Typography,
} from '@mui/material';
import type { TooltipProps } from '@mui/material/Tooltip';
import moment from 'moment';
import React, { useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  checkTherapyPlanning,
  deleteMdsAssessmentByDate,
  getSingleMdsAssessmentAction,
  resetCheckTherapyPlanning,
  resetGetSingleMdsAssessment,
  updateTherapyPlanningDischargeDate,
  updateUncoveredDayTherapyPlanning_2_0Action,
} from 'src/redux/actions/therapyPlanning';
import {
  CloseUncoveredIcon,
  DischargeIcon,
  MergeMdsAssessment,
  MergeMdsAssessmentColor,
  SquareCloseIcon,
  UncoveredDayIcon,
} from './commonComponent/therapy-planning-icons';
import { useTherapyPlanning } from './therapyPlanningProvider';
import { useNavigate } from 'react-router';
import { paths } from 'src/paths';
import { BootstrapDialog } from '../common-modals/BootstrapDialog';
import DeletePopupModal from 'src/components/common-modals/delete-modal';
import { confirmCancelTestIds, dayTestId, therapyPlanningTestId } from './test-ids';

const { therapyPlanning } = paths;

const style = {
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '55%',
  borderRadius: '8px',
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

interface UncoveredDayProps {
  selectedPatient: any;
  day: any;
  index: number;
  uncoveredDay: any;
  mdsAssessments: any;
  setNavigateDate: any;
}

const LightTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip
    {...props}
    classes={{ popper: className }}
  />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: 'white',
    color: 'black',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: ' rgba(0, 0, 0, 0.24) 0px 3px 8px',
    fontSize: 12,
  },
}));

function getCurrentDateUTCWithoutTime(date: any) {
  if (!date) return null;
  // Get the date components
  // const utcDateFormate = moment(date).format('YYYY-MM-DD');
  // Construct the UTC date string with time portion set to 00:00:00.000Z
  const utcDateString = `${date}T00:00:00.000Z`;

  return utcDateString;
}

const convertToUTC = (selectedDate: any) => {
  const selectedDateObj = new Date(selectedDate);
  const selectedYear = selectedDateObj.getFullYear();
  const selectedMonth = selectedDateObj.getMonth();
  const selectedDay = selectedDateObj.getDate();

  const systemDateObj = new Date();
  const systemHours = systemDateObj.getHours();
  const systemMinutes = systemDateObj.getMinutes();
  const systemSeconds = systemDateObj.getSeconds();

  // Create combined Date object
  const combinedDate = new Date(
    selectedYear,
    selectedMonth,
    selectedDay,
    systemHours,
    systemMinutes,
    systemSeconds
  );
  return combinedDate?.toISOString();
};

const UncoveredDay: React.FC<UncoveredDayProps> = ({
  selectedPatient,
  day,
  index,
  uncoveredDay,
  mdsAssessments,
  setNavigateDate,
}) => {
  const [isMdsCalenderHover, setIsMdsCalenderHover] = useState(false);
  const [selectedDischargeDate, setSelectedDischargeDate] = useState<any>(null);
  const getSingleMds = useSelector((state: any) => state.getSingleMdsAssessmentReducer);
  const dateOfServiceResponse = useSelector(
    (state: any) => state?.getDateOfServiceReducer?.response?.data
  );
  const payerDayCountResponse = useSelector(
    (state: any) => state?.getPayerDayCountReducer?.response?.data
  );

  const { weekStartDate } = useTherapyPlanning();

  const [isOpen, setIsOpen] = useState(false);
  const calenderRef = useRef<HTMLDivElement | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const patientInfo = useSelector((state: any) => state?.selectPatientReducer?.patientInfo);

  const getDischargeDatesOfTherapyPlanning2_0 = useSelector(
    (state: any) => state.getTherapyPlanningDischargeDateReducer_2_0?.response?.data
  );
  const isFuturePlanMinutes = useSelector((state: any) => state?.checkTherapyPlanningReducer);
  const providerId = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.providerId
  );
  const [therapyType, setTherapyType] = useState('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [deleteMdsAssessment, setDeleteMdsAssessment] = useState(false);
  const handleDeleteMds = () => {
    dispatch(
      deleteMdsAssessmentByDate({
        patientId: selectedPatient?.uuid,
        param: {
          date: day?.dateFormat,
        },
        startDate: weekStartDate.format('YYYY-MM-DD'),
        endDate: weekStartDate.clone().add(8, 'days').format('YYYY-MM-DD'),
      })
    );
    setDeleteMdsAssessment(false);
  };

  const open = Boolean(anchorEl);

  // THIN-8149: every number on this cell comes from the server-side payer day count; nothing is derived
  // from the episode start in the browser any more.
  const payerDayCountForDate = payerDayCountResponse?.find(
    (item: any) => item?.date === day?.dateFormat
  );
  const mdsDay = payerDayCountForDate?.mdsDay;
  const payerDay = payerDayCountForDate?.payerDay;
  const hasCoveringPayer = payerDayCountForDate?.payerUuid != null;
  const MEDICARE_A_DAY_LIMIT = 100;
  const isDay100 = payerDay === MEDICARE_A_DAY_LIMIT;
  const isPastDay100 = payerDay != null && payerDay > MEDICARE_A_DAY_LIMIT;
  // THIN-8157: the n/100 chip sits on landmark dates only — the first date in view that carries a payer
  // day (so no window is without a reference) and every 10th payer day, which includes day 100.
  const anchorDate = payerDayCountResponse?.find((item: any) => item?.payerDay != null)?.date;
  const showPayerDayChip =
    payerDay != null && (day?.dateFormat === anchorDate || payerDay % 10 === 0);

  const handleClick = (event: any) => {
    if (!hasCoveringPayer) return;
    if (!anchorEl) {
      setSelectedDischargeDate(null);
      setAnchorEl(event.currentTarget);
    } else setAnchorEl(null);
  };

  const handleClose = () => {
    setIsMdsCalenderHover(false);
    setAnchorEl(null);
  };

  useMemo(() => {
    if (!isFuturePlanMinutes?.isFuturePlan && isFuturePlanMinutes?.response?.data === null) return;
    if (!isFuturePlanMinutes?.isFuturePlan && isFuturePlanMinutes?.response?.data !== null) return;

    if (
      isFuturePlanMinutes?.isFuturePlan &&
      isFuturePlanMinutes?.response?.data &&
      selectedDischargeDate
    ) {
      setIsOpen(true);
      dispatch(resetCheckTherapyPlanning());
      return;
    }
    if (
      isFuturePlanMinutes?.isFuturePlan &&
      !isFuturePlanMinutes?.response?.data &&
      selectedDischargeDate
    ) {
      dispatch(
        updateTherapyPlanningDischargeDate({
          ptDischargeDate:
            therapyType === 'PT'
              ? getCurrentDateUTCWithoutTime(selectedDischargeDate)
              : getCurrentDateUTCWithoutTime(
                  getDischargeDatesOfTherapyPlanning2_0?.anticipatedPtDischargeDate
                ),
          otDischargeDate:
            therapyType === 'OT'
              ? getCurrentDateUTCWithoutTime(selectedDischargeDate)
              : getCurrentDateUTCWithoutTime(
                  getDischargeDatesOfTherapyPlanning2_0?.anticipatedOtDischargeDate
                ),
          slpDischargeDate:
            therapyType === 'SLP'
              ? getCurrentDateUTCWithoutTime(selectedDischargeDate)
              : getCurrentDateUTCWithoutTime(
                  getDischargeDatesOfTherapyPlanning2_0?.anticipatedSlpDischargeDate
                ),
          patientId: selectedPatient?.uuid,
          providerId: providerId,
          discipline: therapyType,
        })
      );

      setSelectedDischargeDate(null);
      dispatch(resetCheckTherapyPlanning());
      return;
    }
  }, [isFuturePlanMinutes, selectedDischargeDate]);

  const handleUncoveredDay = () => {
    if (
      !!mdsAssessments?.length &&
      !(uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay)
    ) {
      setDeleteMdsAssessment(true);
      return;
    }
    dispatch(
      updateUncoveredDayTherapyPlanning_2_0Action({
        patientId: selectedPatient?.uuid,
        params: {
          date: day?.dateFormat,
          uncover: !(uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay),
        },
        startDate: weekStartDate.format('YYYY-MM-DD'),
        endDate: weekStartDate.clone().add(8, 'days').format('YYYY-MM-DD'),
      })
    );
    handleClose();
  };

  const handleSetDischarge = (discipline: 'OT' | 'PT' | 'SLP') => {
    setSelectedDischargeDate(day?.dateFormat);
    setTherapyType(discipline);
    dispatch(
      checkTherapyPlanning({
        dischargeDate: day?.dateFormat,
        type: discipline,
        patientId: selectedPatient?.uuid,
      })
    );
    setAnchorEl(null);
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  return (
    <>
      <Box
        sx={{
          // width: `calc(100% / 7)`,
          fontSize: '12px',
          borderRight: '1px solid #F2F4F7',
          height: '100%',
          padding: '3px',
          backgroundColor:
            (uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay) ||
            !!mdsAssessments?.length
              ? '#F2F4F7'
              : 'white',
          color: '#6C737F',
          fontWeight: '500',
          display: 'flex',
          alignItems: 'center',
          marginLeft: '7px',
          justifyContent: 'center',
        }}
      >
        <Stack
          alignItems="center"
          justifyContent={'center'}
          sx={{
            width: '100%',
            borderRadius: '8px',
            background: mdsAssessments?.length
              ? '#F0FDF9'
              : uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay
              ? '#FEE4E2'
              : isMdsCalenderHover && hasCoveringPayer
              ? '#2c394a0a'
              : 'white',
            height: '96%',
          }}
          flexDirection="row"
        >
          <Box />
          {uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay ? (
            <LightTooltip
              placement="top"
              title="Uncovered day"
            >
              <IconButton
                sx={{ width: '32px', height: '32px' }}
                data-testid={dayTestId(day?.dateFormat, 'uncovered')}
              >
                <UncoveredDayIcon />
              </IconButton>
            </LightTooltip>
          ) : (
            <Stack
              flexDirection="row"
              flexWrap="wrap"
              alignItems="center"
              justifyContent="center"
              gap={0.5}
              sx={{ maxWidth: '100%' }}
            >
              {/* THIN-8149: an ARD and the day number share the cell; neither hides the other. */}
              {mdsAssessments?.length ? (
                <Tooltip
                  placement="top"
                  title={
                    <span>
                      {mdsAssessments?.map((mds: any, index: number) => (
                        <div key={index}>{mds?.assessment_type}</div>
                      ))}
                    </span>
                  }
                >
                  <IconButton sx={{ padding: '2px' }}>
                    <MergeMdsAssessmentColor />
                  </IconButton>
                </Tooltip>
              ) : null}
              {mdsDay != null && (
                <LightTooltip
                  placement="bottom"
                  title={
                    <Box sx={{ p: 1, maxWidth: 260 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111927' }}>
                        MDS day {mdsDay}
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#111927', mt: 0.5 }}>
                        Day {mdsDay} of this payer period, counted from the payer start on{' '}
                        {moment(payerDayCountForDate?.payerStartDate).format('MMM D, YYYY')}.
                        Uncovered days are skipped.
                      </Typography>
                      <Typography sx={{ fontSize: '12px', color: '#111927', mt: 1 }}>
                        This is the count the 5-Day assessment window uses.
                      </Typography>
                    </Box>
                  }
                >
                  <Typography
                    variant="caption"
                    sx={{ color: '#6366f1' }}
                    data-testid={dayTestId(day?.dateFormat, 'mds-day')}
                  >
                    {`(${mdsDay})`}
                  </Typography>
                </LightTooltip>
              )}
              {showPayerDayChip && (
                <LightTooltip
                  placement="bottom"
                  title={
                    <Box sx={{ p: 1, maxWidth: 260 }}>
                      <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#111927' }}>
                        {payerDay} of {MEDICARE_A_DAY_LIMIT} Medicare days used
                      </Typography>
                      {isDay100 ? (
                        <Typography sx={{ fontSize: '12px', color: '#111927', mt: 0.5 }}>
                          This is the last covered day under this payer. Add or change the payer to
                          keep planning after it.
                        </Typography>
                      ) : isPastDay100 ? (
                        <Typography sx={{ fontSize: '12px', color: '#111927', mt: 0.5 }}>
                          Past day {MEDICARE_A_DAY_LIMIT} under this payer.
                          {payerDayCountForDate?.blocked
                            ? ' Planning is blocked until the payer changes.'
                            : ''}
                        </Typography>
                      ) : (
                        <>
                          <Typography sx={{ fontSize: '12px', color: '#111927', mt: 0.5 }}>
                            MDS day {mdsDay} plus the{' '}
                            {payerDayCountForDate?.priorMedicareADays ?? 0} prior days recorded on
                            this payer.
                          </Typography>
                          <Typography sx={{ fontSize: '12px', color: '#111927', mt: 1 }}>
                            {MEDICARE_A_DAY_LIMIT - payerDay} days remain
                            {payerDayCountForDate?.day100Date
                              ? ` — day ${MEDICARE_A_DAY_LIMIT} falls on ${moment(
                                  payerDayCountForDate?.day100Date
                                ).format('MMM D, YYYY')}`
                              : ''}
                            .
                          </Typography>
                        </>
                      )}
                      <Typography sx={{ fontSize: '12px', color: '#111927' }}>
                        Marking a day uncovered pushes that date later.
                      </Typography>
                    </Box>
                  }
                >
                  <Chip
                    label={`${payerDay}/${MEDICARE_A_DAY_LIMIT}`}
                    size="small"
                    data-testid={dayTestId(day?.dateFormat, 'payer-day')}
                    sx={{
                      height: '20px',
                      fontSize: '11px',
                      fontWeight: 600,
                      backgroundColor: isDay100 || isPastDay100 ? '#B91C1C' : '#EEF0FE',
                      color: isDay100 || isPastDay100 ? '#FFFFFF' : '#3730A3',
                    }}
                  />
                </LightTooltip>
              )}
            </Stack>
          )}
          {hasCoveringPayer && (
            <MoreVertOutlinedIcon
              fontSize="small"
              sx={{
                fontSize: '14px',
                cursor: 'pointer',
                position: 'absolute',
                top: '2px',
                right: '2px',
              }}
              onClick={handleClick}
              data-testid={dayTestId(day?.dateFormat, 'menu')}
              id="demo-positioned-button"
              aria-haspopup="true"
            />
          )}

          <Menu
            id="basic-menu"
            aria-labelledby="demo-positioned-button"
            anchorEl={anchorEl}
            // sx={{ display: openCalendar ? 'none' : 'initial' }}
            open={open}
            onClose={handleClose}
            MenuListProps={{
              'aria-labelledby': 'basic-button',
            }}
          >
            <MenuItem
              sx={{ gap: '12px', cursor: 'pointer', paddingRight: '32px' }}
              onClick={() => handleUncoveredDay()}
              data-testid={dayTestId(day?.dateFormat, 'menu-uncovered')}
              // disabled={!!mdsAssessments?.length}
            >
              {uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay ? (
                <CloseUncoveredIcon />
              ) : (
                <UncoveredDayIcon />
              )}

              <Typography
                sx={{ fontSize: '12px', color: '#B42318', fontWeight: '500', lineHeight: '20px' }}
              >
                {uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay
                  ? `Cancel 'Uncovered Day'`
                  : `Mark as 'Uncovered Day'`}{' '}
              </Typography>
            </MenuItem>
            <MenuItem
              sx={{ gap: '12px', cursor: 'pointer' }}
              disabled={dateOfServiceResponse?.isOtEvalOnly}
              data-testid={dayTestId(day?.dateFormat, 'menu-discharge-ot')}
              onClick={() => {
                if (getDischargeDatesOfTherapyPlanning2_0?.anticipatedOtDischargeDate) {
                  dispatch(
                    updateTherapyPlanningDischargeDate({
                      otDischargeDate: null,
                      patientId: selectedPatient?.uuid,
                      providerId: providerId,
                      discipline: 'OT',
                    })
                  );
                  setAnchorEl(null);
                } else {
                  handleSetDischarge('OT');
                }
              }}
            >
              {!getDischargeDatesOfTherapyPlanning2_0?.anticipatedOtDischargeDate ? (
                <DischargeIcon />
              ) : (
                <SquareCloseIcon />
              )}

              <Typography
                sx={{ fontSize: '12px', color: '#111927', fontWeight: '500', lineHeight: '20px' }}
              >
                {!getDischargeDatesOfTherapyPlanning2_0?.anticipatedOtDischargeDate
                  ? `Set Anticipated Discharge OT`
                  : `Remove Anticipated Discharge OT`}
              </Typography>
            </MenuItem>
            <MenuItem
              sx={{ gap: '12px', cursor: 'pointer' }}
              disabled={dateOfServiceResponse?.isPtEvalOnly}
              data-testid={dayTestId(day?.dateFormat, 'menu-discharge-pt')}
              onClick={() => {
                if (getDischargeDatesOfTherapyPlanning2_0?.anticipatedPtDischargeDate) {
                  dispatch(
                    updateTherapyPlanningDischargeDate({
                      ptDischargeDate: null,
                      patientId: selectedPatient?.uuid,
                      providerId: providerId,
                      discipline: 'PT',
                    })
                  );
                  setAnchorEl(null);
                } else {
                  handleSetDischarge('PT');
                }
              }}
            >
              {!getDischargeDatesOfTherapyPlanning2_0?.anticipatedPtDischargeDate ? (
                <DischargeIcon />
              ) : (
                <SquareCloseIcon />
              )}
              <Typography
                sx={{ fontSize: '12px', color: '#111927', fontWeight: '500', lineHeight: '20px' }}
              >
                {!getDischargeDatesOfTherapyPlanning2_0?.anticipatedPtDischargeDate
                  ? `Set Anticipated Discharge PT`
                  : `Remove Anticipated Discharge PT`}
              </Typography>
            </MenuItem>
            <MenuItem
              sx={{ gap: '12px', cursor: 'pointer' }}
              disabled={dateOfServiceResponse?.isSlpEvalOnly}
              data-testid={dayTestId(day?.dateFormat, 'menu-discharge-slp')}
              onClick={() => {
                if (getDischargeDatesOfTherapyPlanning2_0?.anticipatedSlpDischargeDate) {
                  dispatch(
                    updateTherapyPlanningDischargeDate({
                      slpDischargeDate: null,
                      patientId: selectedPatient?.uuid,
                      providerId: providerId,
                      discipline: 'SLP',
                    })
                  );
                  setAnchorEl(null);
                } else {
                  handleSetDischarge('SLP');
                }
              }}
            >
              {!getDischargeDatesOfTherapyPlanning2_0?.anticipatedSlpDischargeDate ? (
                <DischargeIcon />
              ) : (
                <SquareCloseIcon />
              )}

              <Typography
                sx={{ fontSize: '12px', color: '#111927', fontWeight: '500', lineHeight: '20px' }}
              >
                {!getDischargeDatesOfTherapyPlanning2_0?.anticipatedSlpDischargeDate
                  ? `Set Anticipated Discharge SLP`
                  : `Remove Anticipated Discharge SLP`}
              </Typography>
            </MenuItem>
            <MenuItem
              sx={{ gap: '12px', cursor: 'pointer' }}
              disabled={
                (uncoveredDay?.length === 9 && uncoveredDay[index]?.isUncoveredDay) ||
                getSingleMds?.loading
              }
              data-testid={dayTestId(day?.dateFormat, 'menu-ard')}
              onClick={() => {
                if (mdsAssessments?.length === 1) {
                  dispatch(resetGetSingleMdsAssessment());
                  setNavigateDate(day?.dateFormat);
                  dispatch(
                    getSingleMdsAssessmentAction({
                      mdsAssessmentId: mdsAssessments?.[0]?.uuid,
                    })
                  );
                  return;
                }
                if (mdsAssessments?.length) {
                  navigate(`${therapyPlanning.index}${therapyPlanning.details}`, {
                    state: {
                      patientId: patientInfo?.uuid,
                      ARD_Date: day?.dateFormat,
                      from: 'ARD_Tab',
                    },
                  });
                  return;
                }
                navigate(
                  `${therapyPlanning.index}${therapyPlanning.details}${therapyPlanning.editDetails}`,
                  {
                    state: {
                      action: 'Create',
                      selectedPatient: patientInfo,
                      from: 'ARD_Tab',
                      ARD_Date: day?.dateFormat,
                    },
                  }
                );
              }}
            >
              <MergeMdsAssessment />
              <Typography
                sx={{ fontSize: '12px', color: '#111927', fontWeight: '500', lineHeight: '20px' }}
              >
                {mdsAssessments?.length ? 'Edit ARD' : 'Set ARD'}
              </Typography>
            </MenuItem>
          </Menu>
        </Stack>
      </Box>
      <Modal
        open={isOpen}
        onClose={handleCloseModal}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box
          ref={calenderRef}
          sx={style}
        >
          <Stack
            flexDirection="row"
            gap="1rem"
            alignItems={'center'}
          >
            {
              <Grid
                container
                alignItems="center"
                justifyContent={'center'}
                sx={{
                  marginBottom: '1rem',
                  width: '65px',
                  minWidth: '55px',
                  height: '50px',
                  borderRadius: '50%',
                  background: 'rgba(247, 144, 9, 0.12)',
                  color: '#B54708',
                }}
              >
                <WarningAmber sx={{ height: '30px', width: '30px' }} />
              </Grid>
            }
            <Stack gap=".75rem">
              <Typography
                id="modal-modal-title"
                variant="h5"
              >
                {'Remove future planned minutes?'}
              </Typography>
              <Typography
                id="modal-modal-title"
                variant="caption2Text"
              >
                {
                  'Selecting an anticipated discharge date will remove any planned minutes and scheduled sessions after this date. Are you sure you want to select this date?'
                }
              </Typography>
            </Stack>
          </Stack>
          <Stack
            flexDirection="row"
            gap="1rem"
            justifyContent="flex-end"
            sx={{ marginTop: '1rem' }}
          >
            <Button
              onClick={() => {
                dispatch(resetCheckTherapyPlanning());
                setSelectedDischargeDate(null);
                setIsOpen(false);
                handleCloseModal();
              }}
              color="inherit"
              data-testid={therapyPlanningTestId('remove-future-minutes', 'cancel')}
            >
              {'Cancel'}
            </Button>
            <Button
              variant="contained"
              color={'primary'}
              data-testid={therapyPlanningTestId('remove-future-minutes', 'confirm')}
              onClick={() => {
                dispatch(
                  updateTherapyPlanningDischargeDate({
                    ptDischargeDate:
                      therapyType === 'PT'
                        ? getCurrentDateUTCWithoutTime(selectedDischargeDate)
                        : getCurrentDateUTCWithoutTime(
                            getDischargeDatesOfTherapyPlanning2_0?.anticipatedPtDischargeDate
                          ),
                    otDischargeDate:
                      therapyType === 'OT'
                        ? getCurrentDateUTCWithoutTime(selectedDischargeDate)
                        : getCurrentDateUTCWithoutTime(
                            getDischargeDatesOfTherapyPlanning2_0?.anticipatedOtDischargeDate
                          ),
                    slpDischargeDate:
                      therapyType === 'SLP'
                        ? getCurrentDateUTCWithoutTime(selectedDischargeDate)
                        : getCurrentDateUTCWithoutTime(
                            getDischargeDatesOfTherapyPlanning2_0?.anticipatedSlpDischargeDate
                          ),
                    patientId: selectedPatient?.uuid,
                    providerId: providerId,
                    discipline: therapyType,
                  })
                );
                dispatch(resetCheckTherapyPlanning());
                setSelectedDischargeDate(null);
                setIsOpen(false);
                handleCloseModal();
              }}
            >
              {'Confirm'}
            </Button>
          </Stack>
        </Box>
      </Modal>
      <BootstrapDialog
        onClose={() => setDeleteMdsAssessment(false)}
        aria-labelledby="customized-dialog-title"
        open={deleteMdsAssessment}
      >
        <DeletePopupModal
          close={() => setDeleteMdsAssessment(false)}
          message={
            'Setting an Uncovered Day will delete the Assessment. This action cannot be undone.'
          }
          button={{ saveLabel: 'Delete', closeLabel: 'Cancel' }}
          title={'Delete MDS Assessment?'}
          handleSaveBtn={handleDeleteMds}
          testIds={confirmCancelTestIds('delete-mds')}
        />
      </BootstrapDialog>
    </>
  );
};

export default UncoveredDay;
